import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  laneApi, LaneClientGroup, LaneStatusCounts, LaneTaskRow, TeamRoster,
} from '../../api/laneApi';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { Claim } from '../../api/claims';
import { Button, ToastMessage } from '../../components/ui';
import { LaneShell, LaneListSlotProps, LaneDetailSlotProps } from './LaneShell';
import ClaimPicker from './ClaimPicker';
import { filtersFromSearchParams } from './laneUrlState';
import { toggleSelect } from './laneSelection';
import { agingLevel, agingLabel, formatShortDate } from './aging';
import { ActorLabel } from './actorLabel';
import { TaskDetailPane } from './TaskDetailPane';
import { MemberPickerModal } from './MemberPickerModal';
import { ArchiveReasonModal } from './ArchiveReasonModal';
import './lanes.css';

/**
 * Client lane body — Tasks Rebuild Task 9. Left list = sender-group
 * accordion (validated design "option A"): group header (sender, open
 * count, oldest-waiting aging colour) expands inline to that sender's
 * emails; a flat-list toggle falls back to a plain per-email list (and is
 * forced on for the Completed sub-tab, since GET /ui/lanes/client/groups
 * is pending-only — see task-7-report.md — and for `archived`/`assigned`,
 * since that endpoint supports neither param either — see `showFlat`).
 *
 * URL params `group` (expanded sender) and `flat` are owned locally here
 * rather than added to the shared laneUrlState.ts, since that file's
 * `filtersFromSearchParams`/tests are pinned to an exact field set and
 * these two params are Client-lane-specific (the Lender lane, Task 10, has
 * no accordion). Everything else (tab/page/pageSize/q/...) still goes
 * through laneUrlState as usual.
 */

const GROUP_PARAM = 'group';
const FLAT_PARAM = 'flat';

function getExpandedGroup(params: URLSearchParams): string | null {
  return params.get(GROUP_PARAM);
}

function applyExpandedGroup(params: URLSearchParams, sender: string | null): URLSearchParams {
  if (sender) params.set(GROUP_PARAM, sender);
  else params.delete(GROUP_PARAM);
  return params;
}

/**
 * Confirm-dialog count phrase for a bulk group action ("Complete all"/
 * "Assign all"). `fetchedCount` is the length of the per-sender task
 * cache (capped at 100 by the /ui/lanes GET, see laneApi.getLaneTasks);
 * `totalCount` is the group's authoritative `taskCount` from the groups
 * endpoint. When the group has more tasks than were fetched, the copy
 * must say so ("the first 100 of 143 emails") rather than silently
 * quoting the capped fetched count as if it were the whole group -- the
 * action itself still only touches the fetched batch.
 */
function describeBulkCount(fetchedCount: number, totalCount: number): string {
  const plural = Math.max(fetchedCount, totalCount) === 1 ? '' : 's';
  if (totalCount > fetchedCount) {
    return `the first ${fetchedCount} of ${totalCount} email${plural}`;
  }
  return `all ${fetchedCount} email${plural}`;
}

function getFlatMode(params: URLSearchParams): boolean {
  return params.get(FLAT_PARAM) === 'yes';
}

function applyFlatMode(params: URLSearchParams, flat: boolean): URLSearchParams {
  if (flat) params.set(FLAT_PARAM, 'yes');
  else params.delete(FLAT_PARAM);
  params.delete('page');
  return params;
}

/**
 * #408 client lane assignment scoping -- Mine/Unassigned/All-staff
 * segmented control. Local to this file, same reasoning as GROUP_PARAM/
 * FLAT_PARAM above: it's Client-lane-specific UI state, not a field the
 * shared laneUrlState.ts's pinned-shape `filtersFromSearchParams` needs to
 * know about. Deliberately a SEPARATE param from `filters.assigned` (the
 * pre-existing, cross-lane AssignedFilterDropdown/omnibox `assigned:<name>`
 * filter, which lets an admin pick ANY roster member and forces flat mode
 * via `showFlat` below) -- this segmented control is the everyday default
 * ("mine by default was the whole point of #408) and stays usable in the
 * grouped accordion view; the two are reconciled in `effectiveAssigned`
 * below, where the advanced per-name filter always wins when it's set.
 *
 * Values are the exact wire values task_lanes._resolve_assigned_param
 * understands ('me' | 'unassigned' | 'all'), so no translation layer is
 * needed between this UI state and the `assigned` query param sent to
 * every fetch. Absent from the URL means the ROLE-BASED DEFAULT (see
 * `getScope`) -- 'me' ("Mine") for non-admins, per Ryan's original #408
 * decision, so a bare `/tasks/client` link still lands a staff member on
 * their own queue.
 *
 * REGRESSION FIX (23/07): that same 'me' default was being applied
 * unconditionally to ADMINS too, which meant an admin landing on a bare
 * `/tasks/client` (no explicit `?scope=`) saw their own -- typically empty
 * -- queue: no groups render there, so none of the bulk entry points
 * (page-wide bulk bar, per-group "Assign all -> staff") ever appeared. The
 * bulk machinery itself was always intact; it was simply unreachable
 * because scope silently defaulted to a queue admins don't use. Admins now
 * default to 'all' when no explicit `?scope=` is present; non-admins are
 * unchanged. An explicit `?scope=` param always wins over the role default
 * for both roles (deep links unchanged).
 */
export type AssignScope = 'me' | 'unassigned' | 'all';

const SCOPE_PARAM = 'scope';

function roleDefaultScope(isAdmin: boolean): AssignScope {
  return isAdmin ? 'all' : 'me';
}

function getScope(params: URLSearchParams, isAdmin: boolean): AssignScope {
  const raw = params.get(SCOPE_PARAM);
  if (raw === 'me' || raw === 'unassigned' || raw === 'all') return raw;
  return roleDefaultScope(isAdmin);
}

function applyScope(params: URLSearchParams, scope: AssignScope, isAdmin: boolean): URLSearchParams {
  // Only omit the param when it matches this role's default -- otherwise an
  // admin explicitly picking 'me' (whose role default is 'all') would have
  // their choice silently discarded on the next render (the URL would carry
  // no scope, which getScope would then read back as 'all' again).
  if (scope === roleDefaultScope(isAdmin)) params.delete(SCOPE_PARAM);
  else params.set(SCOPE_PARAM, scope);
  params.delete('page');
  return params;
}

/** Bridges LaneShell's internal `refreshToken` (only visible inside the
 * render-prop slots) up to this component's own fetch effects, so the
 * shell's Refresh button re-triggers the group/flat/sender fetches this
 * file owns without LaneShell needing to know about them. */
const RefreshBridge: React.FC<{ token: number; onChange: (t: number) => void }> = ({ token, onChange }) => {
  useEffect(() => { onChange(token); }, [token, onChange]);
  return null;
};

/** Item 4 (lane-feedback batch 28/07): same bridging problem as
 * RefreshBridge above, for LaneListSlotProps.showToast -- the post-assign
 * "Assigned N to <name>" toast fires from confirmAssignMemberAll/
 * handlePageBulkAssignMemberConfirm, which are plain component-level
 * handlers with no direct access to the render-prop slot's `showToast`.
 * `showToast` is a stable useCallback in LaneShell (empty deps), so this
 * only actually re-fires the effect once, on mount. */
const ToastBridge: React.FC<{ fn: (t: ToastMessage) => void; onChange: (fn: (t: ToastMessage) => void) => void }> = ({ fn, onChange }) => {
  useEffect(() => { onChange(() => fn); }, [fn, onChange]);
  return null;
};

interface BulkProgress {
  sender: string;
  action: 'assign' | 'complete' | 'assignMember';
  done: number;
  total: number;
  failed: number;
  finished: boolean;
}

/**
 * Page-wide select-all + bulk bar for the grouped (accordion) view -- live-
 * use bug fix (BUG 2): the accordion only ever had per-group actions
 * ("Assign all -> claim" / "Complete all" / "Assign all -> staff" on a
 * single sender's header), with no way to act across every sender rendered
 * on the page at once, unlike Lender/Manual's checkbox-driven bulk bar.
 *
 * Deliberately a SEPARATE action/progress/result type from the per-sender
 * `BulkProgress` above (which some sender's group header row still uses for
 * its own "Assign all"/"Complete all" inline progress) -- the two bulk
 * flows are independent and can be in flight/hold state at the same time
 * (e.g. a per-group "Complete all" on one sender while a page-wide
 * selection spanning other senders is still checked).
 *
 * Verb/suffix wording and the Complete/Archive/AssignMember action set are
 * lifted verbatim from LenderLane's identical `BulkAction`/
 * `BULK_ACTION_PROGRESS_VERB`/`BULK_ACTION_RESULT_SUFFIX` (that lane's
 * bulk bar is the pinned reference implementation this task asked to
 * reuse) -- "assign" (bulk-assign-to-claim) and "forward" are intentionally
 * NOT included here: the accordion already has a per-group "Assign all ->
 * claim" action, and a page-wide selection spans multiple senders at once,
 * so a single-target Forward (one subject/body prefill) has no sensible
 * multi-sender behaviour the way it does for Lender's single flat list.
 */
type PageBulkAction = 'complete' | 'assignMember' | 'archive';

const PAGE_BULK_PROGRESS_VERB: Record<PageBulkAction, string> = {
  complete: 'Completing',
  assignMember: 'Assigning',
  archive: 'Archiving',
};
const PAGE_BULK_RESULT_SUFFIX: Record<PageBulkAction, string> = {
  complete: 'completed',
  assignMember: 'assigned',
  archive: 'archived',
};

export const ClientLane: React.FC = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  const expandedGroup = getExpandedGroup(searchParams);
  const flatMode = getFlatMode(searchParams);
  // Completed sub-tab has no groups endpoint support -- always flat there.
  // Task 13: an archived:yes omnibox chip has the same problem -- GET
  // /ui/lanes/client/groups (task_lane_service.client_sender_groups) only
  // ever queries the live email_tasks table and has no archived param at
  // all, so silently staying in grouped mode would show "No pending sender
  // groups" while archived matches exist elsewhere -- an actively
  // misleading empty state, not just an inert no-op. Forcing flat mode
  // (which does support `archived`, see fetchFlat below) makes archived:yes
  // actually reach real results instead of hiding them.
  //
  // Live-use bug fix (historical): `assigned` used to get the exact same
  // treatment as `archived` above -- GET /ui/lanes/client/groups had NO
  // assigned param at all, so the toolbar's AssignedFilterDropdown/omnibox
  // `assigned:` chip forced flat mode as a workaround (fetchGroups() was
  // "assigned-blind", so staying grouped would have silently ignored the
  // picked name). #408 later taught the groups endpoint an `assigned`
  // filter (see `scope`/`effectiveAssigned` below) to serve the Mine/
  // Unassigned/All-staff segmented control -- but the flat-forcing here
  // was left in place for the advanced per-name filter specifically.
  //
  // Item 1a (lane-feedback batch 28/07 -- Ryan: "why do we not group tasks
  // by client name when you're looking at your own tasks or assigned
  // ones?"): that flat-forcing is now removed. The groups endpoint's
  // `assigned` filter (task_lane_service.client_sender_groups) already
  // accepts an exact team-member name -- not just the 'me'/'unassigned'/
  // 'all' literals the segmented control sends -- via the same
  // _apply_assigned_filter every other assigned-scoped query uses (see
  // that function's docstring). `effectiveAssigned` below already resolves
  // the advanced per-name filter with the same precedence it always has
  // (filters.assigned wins over the segmented control), and threads it into
  // fetchGroups/fetchSenderTasks unchanged -- so picking a specific
  // assignee now keeps the accordion grouped instead of silently falling
  // back to a flat list. The manual "Flat list" toggle remains available on
  // top of this for anyone who prefers the flat view regardless of filter.
  const showFlat = filters.tab === 'completed' || flatMode || filters.archived;

  const scope = getScope(searchParams, isAdmin);
  // The advanced per-name filter always wins over the segmented control
  // when both are somehow present (shouldn't normally happen -- the
  // control is hidden/disabled while filters.assigned is set, see
  // renderList below -- but a hand-edited URL could combine them).
  // scope 'all' means "no filter", matching back-compat semantics the
  // backend already applies to an absent `assigned` param.
  const effectiveAssigned = filters.assigned || (scope === 'all' ? undefined : scope);

  /** BUG 2 fix (live-use report): under the 'Unassigned' scope, the
   * page-wide select-all checkbox / bulk bar (Complete/Archive/[admin:
   * Assign to staff]) / per-row checkboxes were unreachable -- they were
   * never actually gated on `scope` at all (any non-empty group list
   * showed them, 'me' included), so the only thing that ever hid them in
   * practice was an empty group list, which made "Unassigned has no bulk
   * controls" look like an intentional restriction rather than the
   * coincidence it was. Hiding page-wide bulk under 'me' (a single
   * assigned person's own queue) is a deliberate, sensible restriction --
   * but 'unassigned' is exactly where bulk-assign-to-staff is needed
   * (triaging an unassigned queue), so it must behave the same as 'all'
   * here. Independent of admin status -- only the "Assign to staff" button
   * inside the bar stays admin-gated, unchanged from before. */
  const pageWideBulkAvailable = scope !== 'me';

  // #408: the signed-in user's own TEAM_MEMBERS roster name is needed for
  // the cross-staff bulk-action warning under the All-staff view (see
  // otherStaffCount below). `null` while loading/unfetched/not-a-team-member
  // simply means that warning never fires, which is the safe default (no
  // false "belongs to other staff" claims from an unresolved identity).
  //
  // Item 3 (lane-feedback batch 28/07): this fetch used to discard
  // everything except currentUserTeamName. The whole response (members +
  // unassignedCount, each member's pendingCount already scoped to this lane
  // via `getTeamRoster('client')`, matching the existing lane-scoping fix
  // MemberPickerModal/AssignedFilterDropdown already rely on) now feeds a
  // workload chip strip in the toggle row below, so the roster itself is
  // kept as state rather than just the one derived field.
  const [roster, setRoster] = useState<TeamRoster | null>(null);
  const fetchRoster = useCallback(() => {
    laneApi.getTeamRoster('client')
      .then((r) => setRoster(r))
      .catch(() => {
        // Non-fatal: the cross-staff warning and the workload strip just
        // don't render for this session, same fallback AssignedFilterDropdown
        // already uses for a failed roster fetch.
      });
  }, []);
  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  const currentUserTeamName = roster?.currentUserTeamName ?? null;

  /** #408: how many of `tasks` are assigned to someone OTHER than the
   * signed-in user -- the confirm-dialog count the All-staff view's bulk
   * actions must surface ("N belong to other staff"). Only meaningful
   * (and only ever called) under `scope === 'all'`; an unmapped/unresolved
   * `currentUserTeamName` treats every assigned task as "other" rather
   * than silently under-counting. */
  const otherStaffCount = useCallback((tasks: LaneTaskRow[]): number => tasks.filter(
    (t) => !!t.assignedToTeamMember && t.assignedToTeamMember !== currentUserTeamName,
  ).length, [currentUserTeamName]);

  const otherStaffSuffix = (tasks: LaneTaskRow[]): string => {
    if (scope !== 'all') return '';
    const n = otherStaffCount(tasks);
    if (n === 0) return '';
    return ` (${n} belong${n === 1 ? 's' : ''} to other staff)`;
  };

  const [shellRefreshToken, setShellRefreshToken] = useState(0);
  // Item 4: see ToastBridge's doc comment above.
  const [showToastFn, setShowToastFn] = useState<(t: ToastMessage) => void>(() => () => {});

  const [groups, setGroups] = useState<LaneClientGroup[]>([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  // Task 15 (John Lowry follow-up): the grouped accordion's own
  // statusCounts, from GET /ui/lanes/client/groups (LaneClientGroupsResponse)
  // -- previously that response had no such field at all, so the DEFAULT
  // grouped view showed no Pending/Completed numbers (only flat mode did,
  // via flatStatusCounts below). `undefined` pre-fetch; each fetchGroups
  // call sets a fresh value scoped to whatever filters produced it, same
  // "never stale" discipline flatStatusCounts already follows.
  const [groupsStatusCounts, setGroupsStatusCounts] = useState<LaneStatusCounts | undefined>(undefined);

  const [flatRows, setFlatRows] = useState<LaneTaskRow[]>([]);
  const [flatTotal, setFlatTotal] = useState(0);
  // Task 11: pending/completed split for the toggle buttons -- the flat
  // fetch (GET /ui/lanes/client, same route Lender/Manual use) carries
  // statusCounts; the grouped accordion's own equivalent is
  // groupsStatusCounts above (Task 15). `renderList`/LaneShell below picks
  // whichever of the two matches the active mode -- never a stale count
  // left over from before a switch between flat and grouped.
  const [flatStatusCounts, setFlatStatusCounts] = useState<LaneStatusCounts | undefined>(undefined);
  const [flatLoading, setFlatLoading] = useState(false);
  const [flatError, setFlatError] = useState<string | null>(null);

  const [senderTasks, setSenderTasks] = useState<Record<string, LaneTaskRow[]>>({});
  const [senderLoading, setSenderLoading] = useState<Record<string, boolean>>({});
  const [senderError, setSenderError] = useState<Record<string, string | null>>({});

  // #408: this per-sender cache is scope-specific (each entry is "this
  // sender's tasks under the CURRENTLY active assigned filter"). A scope
  // change re-fetches only the currently-expanded group (see the
  // fetchSenderTasks effect above); every OTHER sender's cached entry would
  // otherwise silently keep showing the previous scope's tasks if later
  // re-expanded, or (worse) feed stale rows into "Select all on page" /
  // the page-wide bulk bar's `allFetchedTasksById` lookup. Dropping the
  // whole cache on scope change forces every sender to re-fetch under the
  // new scope the next time it's touched.
  useEffect(() => {
    setSenderTasks({});
    setSenderLoading({});
    setSenderError({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAssigned]);

  const [assignAllFor, setAssignAllFor] = useState<{ sender: string; displayName: string; taskCount: number } | null>(null);
  // The debounced search term/results/searching state that used to live here
  // now lives inside the shared ClaimPicker component (Task 18 dedupe); only
  // the parent-controlled selection remains, since confirmAssignAll reads it.
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);

  // Staff assignment restoration (fast-follow) -- ClientLane's group-level
  // equivalent of the other three lanes' checkbox-driven bulk bar: "Assign
  // all -> staff" alongside the pre-existing "Assign all -> claim"/"Complete
  // all" per-sender group actions, via the same shared MemberPickerModal.
  const [assignMemberAllFor, setAssignMemberAllFor] = useState<{ sender: string; displayName: string; taskCount: number } | null>(null);

  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);

  // --- BUG 2: page-wide select-all + bulk bar (grouped view only) ---
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  // True while "Select all on page" is lazily fetching any not-yet-expanded
  // sender's tasks (see toggleSelectAllOnPage below).
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [pageBulkProgress, setPageBulkProgress] = useState<
    { action: PageBulkAction; done: number; total: number; failed: number } | null
  >(null);
  // Outlives `checkedIds` -- same reasoning as LenderLane's identical
  // `bulkResult` (runPageBulk clears `checkedIds` in the same batch it sets
  // this, so the banner must render outside any `checkedIds.size > 0` gate
  // or a partial-failure result would vanish the instant it appears).
  const [pageBulkResult, setPageBulkResult] = useState<
    { action: PageBulkAction; total: number; failed: number } | null
  >(null);
  const [pageBulkAssignMemberOpen, setPageBulkAssignMemberOpen] = useState(false);
  const [pageBulkAssigningMember, setPageBulkAssigningMember] = useState(false);
  const [pageBulkArchiveOpen, setPageBulkArchiveOpen] = useState(false);
  const [pageBulkArchiving, setPageBulkArchiving] = useState(false);
  // Claim-skip partition (Task 12 review fix, same rule LenderLane's bulk
  // Archive enforces) -- computed at click-time in handlePageBulkArchiveClick.
  const [pageArchivableIds, setPageArchivableIds] = useState<string[]>([]);
  const [pageArchiveSkippedCount, setPageArchiveSkippedCount] = useState(0);
  const [pageArchiveBlockedMessage, setPageArchiveBlockedMessage] = useState<string | null>(null);

  // Flattened view of every sender's fetched-task cache, keyed by task id --
  // lets the page-wide bulk bar look up a checked id's `assignedClaimId`
  // (for the archive claim-skip partition) regardless of which sender group
  // it came from, without a second parallel id->row map to keep in sync.
  const allFetchedTasksById = useMemo(() => {
    const map: Record<string, LaneTaskRow> = {};
    Object.values(senderTasks).forEach((tasks) => {
      tasks.forEach((t) => { map[t.id] = t; });
    });
    return map;
  }, [senderTasks]);

  // The Completed tab opened on the OLDEST completions. Neither task fetch
  // below sent sortBy/sortOrder, so the route's own defaults applied
  // (task_lanes.py: sortBy='createdAt', sortOrder='asc') and page 1 showed
  // tasks created a year ago while the one just completed sat thousands of
  // rows down -- "I don't know where it ends up" is largely this. Pending
  // stays oldest-first: that tab genuinely is a work queue. Same rule
  // LenderLane applies via its defaultSortChoice; ClientLane has no sort
  // control of its own, so an explicit ?sortBy in the URL still wins.
  //
  // The sort key is completedAt, NOT createdAt: they diverge exactly where
  // this matters. An email received in June and completed this morning is
  // the newest completion but nearly the oldest creation, so createdAt desc
  // would still bury it -- backlog work is precisely what staff go to this
  // tab to confirm. See SORT_MAP in task_lane_service.py.
  const completedTab = filters.tab === 'completed';
  const listSortBy = filters.sortBy || (completedTab ? 'completedAt' : undefined);
  const listSortOrder = filters.sortOrder || (completedTab ? 'desc' : undefined);

  const fetchGroups = useCallback(() => {
    setGroupsLoading(true);
    setGroupsError(null);
    laneApi.getClientGroups({
      page: filters.page,
      pageSize: filters.pageSize,
      sortBy: 'oldestReceived',
      sortOrder: 'asc',
      search: filters.q || undefined,
      // #408: Mine/Unassigned/All-staff segmented control -- the advanced
      // per-name filter (filters.assigned) always wins when both are set,
      // same precedence as effectiveAssigned everywhere else.
      assigned: effectiveAssigned,
      // Item 1b (lane-feedback batch 28/07): the omnibox's from: chip used
      // to be silently dropped in grouped mode -- the groups endpoint had
      // no senderEmail support at all, so fetchGroups() never forwarded it
      // (unlike fetchFlat below, which always has). Now that
      // client_sender_groups filters on the same live-table predicate
      // list_lane's flat path uses, forwarding it here narrows the
      // accordion to the matching sender's group(s) instead of a no-op.
      senderEmail: filters.senderEmail || undefined,
    })
      .then((res) => {
        setGroups(res.groups);
        setGroupsTotal(res.total);
        setGroupsStatusCounts(res.statusCounts);
      })
      .catch(() => setGroupsError('Failed to load sender groups.'))
      .finally(() => setGroupsLoading(false));
  }, [filters.page, filters.pageSize, filters.q, filters.senderEmail, effectiveAssigned]);

  const fetchFlat = useCallback(() => {
    setFlatLoading(true);
    setFlatError(null);
    laneApi.getLaneTasks('client', {
      statusTab: filters.tab,
      page: filters.page,
      pageSize: filters.pageSize,
      // Task 13: omnibox operators thread through the flat list the same
      // way LenderLane's do -- see that file's identical additions for the
      // reasoning. The grouped accordion view (fetchGroups, below) has no
      // equivalent for from:/claim: -- a legitimate no-op while grouped
      // (documented in the Task 13 report). `archived` is handled by
      // forcing flat mode (see `showFlat` above); `assigned` now applies in
      // BOTH views via `effectiveAssigned` (#408).
      receivedFrom: filters.receivedFrom || undefined,
      // #4: until: -> receivedTo, the range-partner to receivedFrom that
      // LenderLane threads through the same way (see that file's identical
      // addition) -- combines with receivedFrom into a since..until range.
      receivedTo: filters.until || undefined,
      senderEmail: filters.senderEmail || undefined,
      claim: filters.claim || undefined,
      assigned: effectiveAssigned,
      archived: filters.archived || undefined,
      q: filters.q || undefined,
      sortBy: listSortBy,
      sortOrder: listSortOrder,
    })
      .then((res) => { setFlatRows(res.tasks); setFlatTotal(res.total); setFlatStatusCounts(res.statusCounts); })
      .catch(() => setFlatError('Failed to load tasks.'))
      .finally(() => setFlatLoading(false));
  }, [
    filters.tab, filters.page, filters.pageSize, filters.q,
    filters.receivedFrom, filters.until, filters.senderEmail, filters.claim, filters.archived, effectiveAssigned,
    listSortBy, listSortOrder,
  ]);

  const fetchSenderTasks = useCallback((sender: string): Promise<LaneTaskRow[]> => {
    setSenderLoading((p) => ({ ...p, [sender]: true }));
    setSenderError((p) => ({ ...p, [sender]: null }));
    return laneApi.getLaneTasks('client', {
      senderEmail: sender, statusTab: filters.tab, page: 1, pageSize: 100,
      // #408: the accordion's per-sender body (ticket #408's "drawer") must
      // stay in lockstep with whatever scope fetchGroups used to produce
      // the group list this sender came from -- otherwise expanding a
      // group counted as "3 of mine" would reveal other staff's emails
      // from the same sender, which was the exact reported bug.
      assigned: effectiveAssigned,
      // Same newest-first-when-Completed rule as fetchFlat above -- an
      // expanded group's body defaulted to oldest-first for the same reason.
      sortBy: listSortBy,
      sortOrder: listSortOrder,
    })
      .then((res) => {
        setSenderTasks((p) => ({ ...p, [sender]: res.tasks }));
        return res.tasks;
      })
      .catch(() => {
        setSenderError((p) => ({ ...p, [sender]: 'Failed to load emails for this sender.' }));
        return [];
      })
      .finally(() => setSenderLoading((p) => ({ ...p, [sender]: false })));
  }, [filters.tab, effectiveAssigned, listSortBy, listSortOrder]);

  useEffect(() => {
    if (showFlat) fetchFlat(); else fetchGroups();
  }, [showFlat, fetchFlat, fetchGroups, shellRefreshToken]);

  useEffect(() => {
    if (expandedGroup && !showFlat) fetchSenderTasks(expandedGroup);
    // #408: re-fetch an already-expanded sender's body when the segmented
    // control (or the advanced per-name filter) changes scope -- otherwise
    // flipping Mine -> All staff mid-session would leave a stale,
    // still-scoped task list showing under a now-differently-scoped group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedGroup, showFlat, shellRefreshToken, effectiveAssigned]);

  // Page-wide selection (and any leftover bulk result/blocked-archive
  // banner) is transient UI state, not URL state -- clear it whenever the
  // page/tab/query changes (a new set of groups is now on the page) or the
  // view leaves grouped mode entirely (flat has no equivalent selection
  // UI in this iteration). Same treatment LenderLane/ManualLane give their
  // own `checkedIds`. #408: also clear on a scope change -- the page's
  // group/row set just changed underneath any existing selection.
  useEffect(() => {
    setCheckedIds(new Set());
    setPageBulkResult(null);
    setPageArchiveBlockedMessage(null);
  }, [filters.tab, filters.page, filters.q, showFlat, effectiveAssigned]);

  const toggleChecked = (id: string) => {
    setPageBulkResult(null);
    setPageArchiveBlockedMessage(null);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // "Select all on page" selects EVERY task of EVERY sender group currently
  // rendered on the page -- not just the ones already expanded. Any group
  // whose tasks aren't cached yet (senderTasks[g.sender] === undefined) is
  // fetched lazily here via the same fetchSenderTasks the accordion's own
  // expand-on-click uses, so it never needs its own separate fetch path.
  //
  // Each per-sender fetch is capped at 100 (see laneApi.getLaneTasks doc) --
  // a sender group with more pending tasks than that only has its first 100
  // selected. Rather than silently under-selecting, this surfaces that gap
  // via `showToast` (the same "quote true totals" pattern the per-group
  // Assign-all/Complete-all confirms already use via `describeBulkCount`,
  // just as an info toast instead of confirm copy, since select-all itself
  // has no confirm step).
  const allGroupsFetched = groups.length > 0 && groups.every((g) => senderTasks[g.sender] !== undefined);
  const allOnPageChecked = allGroupsFetched && checkedIds.size > 0
    && groups.every((g) => (senderTasks[g.sender] || []).every((t) => checkedIds.has(t.id)));

  const toggleSelectAllOnPage = async (showToast: (t: ToastMessage) => void) => {
    if (allOnPageChecked) {
      setCheckedIds(new Set());
      setPageBulkResult(null);
      setPageArchiveBlockedMessage(null);
      return;
    }
    setSelectAllLoading(true);
    const perGroupTasks = await Promise.all(
      groups.map((g) => senderTasks[g.sender] || fetchSenderTasks(g.sender)),
    );
    setSelectAllLoading(false);

    const ids = new Set<string>();
    perGroupTasks.forEach((tasks) => (tasks || []).forEach((t) => ids.add(t.id)));
    setCheckedIds(ids);
    setPageBulkResult(null);
    setPageArchiveBlockedMessage(null);

    const fetchedCount = ids.size;
    const trueTotal = groups.reduce((sum, g) => sum + g.taskCount, 0);
    if (trueTotal > fetchedCount) {
      showToast({
        type: 'info',
        message: `Selected ${describeBulkCount(fetchedCount, trueTotal)} across ${groups.length} sender${groups.length === 1 ? '' : 's'} (100-per-sender fetch cap).`,
      });
    }
  };

  // Shared per-item-failure-isolated bulk runner for the page-wide bar --
  // same shape as LenderLane/ManualLane's `runBulk` (Task 12), reused
  // verbatim: sequential so one failure doesn't abort the rest, live
  // progress, then a `pageBulkResult` summary that outlives `checkedIds`.
  // Also refreshes every currently-cached sender group (not just the ones
  // touched) plus the group list itself, since an id acted on on this page
  // is guaranteed to belong to an already-cached sender (per-row checkboxes
  // only exist inside an expanded, already-fetched group; select-all fetches
  // every rendered group up front) -- there is no cheaper "only the affected
  // senders" set to compute that isn't just this same cache's key list.
  const runPageBulk = async (action: PageBulkAction, ids: string[], fn: (id: string) => Promise<any>) => {
    setPageBulkResult(null);
    setPageBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await fn(ids[i]);
      } catch {
        failed += 1;
      }
      setPageBulkProgress({ action, done: i + 1, total: ids.length, failed });
    }
    setPageBulkProgress(null);
    setPageBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchGroups();
    // Item 3: complete/archive change whoever the affected tasks were
    // assigned to's pending workload too (a completed/archived task no
    // longer counts as pending) -- refetch the strip alongside the group
    // list, same "piggyback the existing post-action refresh" as every
    // other fetchRoster() call site in this file.
    fetchRoster();
    Object.keys(senderTasks).forEach((sender) => fetchSenderTasks(sender));
  };

  // Single-request variant of runPageBulk (perf fix -- USER REPORT: bulk
  // "Assign to staff" on 67 selected crawled at "Assigning 1/67" because it
  // drove 67 sequential POST /ui/team-tasks/bulk-assign calls, one per id,
  // even though that route already accepts the full taskIds array and does
  // one SQL UPDATE for the lot. `fn` here takes the WHOLE id list and
  // resolves to however many the server actually matched/updated
  // (bulk_assign_tasks silently skips ids it can't find rather than
  // erroring -- no per-id success/failure to report, just a partial
  // *count*), so progress is indeterminate (no per-item ticks) then jumps
  // straight to the result banner once the single request settles.
  // Returns the server-reported succeeded count -- item 4 (post-assign
  // toast) needs the true number assigned, not just the total requested,
  // since a partial match (see the doc comment above) means fewer than
  // `ids.length` actually moved.
  const runPageBulkOneShot = async (
    action: PageBulkAction,
    ids: string[],
    fn: (ids: string[]) => Promise<number>,
  ): Promise<number> => {
    setPageBulkResult(null);
    setPageBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    let succeeded = 0;
    try {
      succeeded = await fn(ids);
      failed = Math.max(0, ids.length - succeeded);
    } catch {
      failed = ids.length;
    }
    setPageBulkProgress(null);
    setPageBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchGroups();
    // Item 3: an assignMember one-shot changes both the newly-assigned
    // member's and (for a reassign) the previous assignee's pending count.
    fetchRoster();
    Object.keys(senderTasks).forEach((sender) => fetchSenderTasks(sender));
    return succeeded;
  };

  const handlePageBulkComplete = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;
    const { completable, skipped } = partitionPageCompletable(ids);
    if (completable.length === 0) {
      window.alert("None of the selected emails can be completed — a client email has to be linked to a claim first.");
      return;
    }
    const plural = completable.length === 1 ? '' : 's';
    // Every id here came from an already-fetched row (per-row checkbox or
    // select-all), so this is always the true, uncapped count being acted
    // on -- the select-all toast above is the one place a capped-vs-true
    // discrepancy can exist, and it's already been surfaced there.
    // #408: under the All-staff view, call out how many of the selected
    // tasks belong to someone other than the signed-in user.
    const checkedTasks = completable.map((id) => allFetchedTasksById[id]).filter(Boolean) as LaneTaskRow[];
    const skipSuffix = skipped > 0 ? ` ${skipped} not linked to a claim will be skipped.` : '';
    const ok = window.confirm(
      `Complete ${completable.length} email${plural}?${skipSuffix}${otherStaffSuffix(checkedTasks)}`,
    );
    if (!ok) return;
    await runPageBulk('complete', completable, (id) => unifiedTasksApi.completeTask(id));
  };

  const handlePageBulkAssignMemberConfirm = async (member: string | null) => {
    const ids = Array.from(checkedIds);
    setPageBulkAssignMemberOpen(false);
    if (ids.length === 0) return;
    setPageBulkAssigningMember(true);
    const succeeded = await runPageBulkOneShot('assignMember', ids, (allIds) => laneApi.assignMember(allIds, member).then((r) => r.assigned));
    setPageBulkAssigningMember(false);
    // Item 4 (lane-feedback batch 28/07): only for a real assignment (not
    // the Unassign option -- "Assigned N to <name>" doesn't make sense for
    // that), and only under the All-staff scope -- under Unassigned the
    // acted-on rows vanish from the current view already (no toast needed,
    // per Ryan's adjudicated wording); under Mine, page-wide bulk assign
    // isn't even reachable (pageWideBulkAvailable is scope !== 'me').
    if (member && scope === 'all' && succeeded > 0) {
      showToastFn({
        type: 'success',
        message: `Assigned ${succeeded} to ${member} — switch to Unassigned to keep triaging`,
      });
    }
  };

  // Partitions checked ids into archivable (no assignedClaimId) vs skipped
  // (assigned to a claim) -- same claim-skip rule LenderLane's bulk Archive
  // enforces (TaskDetailPane's single-task `canArchive` gate refuses a
  // claim-assigned task; bulk must too, or checking a claim-assigned row
  // into a page-wide selection would silently archive something the
  // single-task UI would never let through).
  const partitionPageArchivable = (ids: string[]) => {
    const archivable = ids.filter((id) => !allFetchedTasksById[id]?.assignedClaimId);
    return { archivable, skipped: ids.length - archivable.length };
  };

  // The mirror image of partitionPageArchivable, and the reason the two read
  // as one rule: a client email needs a claim link to be COMPLETED, and needs
  // the absence of one to be ARCHIVED. Link it and complete it, or archive
  // it. The backend 409s an unlinked completion whatever the UI does (that is
  // the real guard) -- partitioning here just stops a bulk selection firing N
  // requests that are all going to be refused.
  const partitionPageCompletable = (ids: string[]) => {
    const completable = ids.filter((id) => !!allFetchedTasksById[id]?.assignedClaimId);
    return { completable, skipped: ids.length - completable.length };
  };

  const handlePageBulkArchiveClick = () => {
    const ids = Array.from(checkedIds);
    const { archivable, skipped } = partitionPageArchivable(ids);
    if (archivable.length === 0) {
      setPageArchiveBlockedMessage("None of the selected tasks can be archived — tasks assigned to a claim can't be archived.");
      return;
    }
    setPageArchiveBlockedMessage(null);
    setPageArchivableIds(archivable);
    setPageArchiveSkippedCount(skipped);
    setPageBulkArchiveOpen(true);
  };

  const handlePageBulkArchiveConfirm = async (reason: string) => {
    const ids = pageArchivableIds;
    setPageBulkArchiveOpen(false);
    if (ids.length === 0) return;
    setPageBulkArchiving(true);
    await runPageBulk('archive', ids, (id) => unifiedTasksApi.archiveTask(id, reason));
    setPageBulkArchiving(false);
  };

  const toggleGroup = (sender: string) => {
    setSearchParams((prev) => applyExpandedGroup(prev, expandedGroup === sender ? null : sender));
  };

  const setFlat = (flat: boolean) => {
    setSearchParams((prev) => applyFlatMode(prev, flat));
  };

  const setScope = (next: AssignScope) => {
    setSearchParams((prev) => applyScope(prev, next, isAdmin));
  };

  const closeAssignAll = () => {
    setAssignAllFor(null);
    setSelectedClaim(null);
  };

  const openAssignAll = async (g: LaneClientGroup) => {
    let tasks = senderTasks[g.sender];
    if (!tasks) tasks = await fetchSenderTasks(g.sender);
    if (!tasks || tasks.length === 0) {
      window.alert('No emails to assign for this sender.');
      return;
    }
    setAssignAllFor({
      sender: g.sender,
      displayName: g.displayName || g.clientName || g.sender,
      taskCount: g.taskCount,
    });
  };

  const confirmAssignAll = async () => {
    if (!assignAllFor || !selectedClaim) return;
    const tasks = senderTasks[assignAllFor.sender] || [];
    const count = tasks.length;
    const claimLabel = `${selectedClaim.firstName || ''} ${selectedClaim.lastName || ''}`.trim() || selectedClaim.id;
    const ok = window.confirm(
      `Assign ${describeBulkCount(count, assignAllFor.taskCount)} from ${assignAllFor.displayName} to claim ${claimLabel}?${otherStaffSuffix(tasks)}`,
    );
    if (!ok) return;
    const { sender } = assignAllFor;
    const claimId = selectedClaim.id;
    closeAssignAll();
    setBulkProgress({ sender, action: 'assign', done: 0, total: count, failed: 0, finished: false });
    let failed = 0;
    for (let i = 0; i < tasks.length; i++) {
      try {
        await unifiedTasksApi.assignTask(tasks[i].id, claimId, tasks[i].emailType || undefined);
      } catch {
        failed += 1;
      }
      setBulkProgress({ sender, action: 'assign', done: i + 1, total: count, failed, finished: i + 1 === count });
    }
    fetchGroups();
    // Item 3: assign-to-CLAIM doesn't change assignedToTeamMember, so this
    // is a no-op for the workload strip in practice -- refetching anyway
    // keeps this call site consistent with every other post-action refresh
    // in this file rather than special-casing the one action that happens
    // not to move the numbers.
    fetchRoster();
    fetchSenderTasks(sender);
  };

  const handleCompleteAll = async (g: LaneClientGroup) => {
    let tasks = senderTasks[g.sender];
    if (!tasks) tasks = await fetchSenderTasks(g.sender);
    if (!tasks || tasks.length === 0) {
      window.alert('No emails to complete for this sender.');
      return;
    }
    // Same claim-link partition the page-wide bulk bar applies -- see
    // partitionPageCompletable.
    const completable = tasks.filter((t) => !!t.assignedClaimId);
    const skipped = tasks.length - completable.length;
    if (completable.length === 0) {
      window.alert("None of these emails can be completed — a client email has to be linked to a claim first. Use Assign all to link them to one.");
      return;
    }
    const count = completable.length;
    const label = g.displayName || g.clientName || g.sender;
    // describeBulkCount's "the first N of M" framing describes a fetch cap,
    // not a skip -- once anything is skipped the plain count is the honest
    // number, and the suffix says what happened to the rest.
    const countPhrase = skipped > 0
      ? `${count} email${count === 1 ? '' : 's'}`
      : describeBulkCount(count, g.taskCount);
    const skipSuffix = skipped > 0 ? ` ${skipped} not linked to a claim will be skipped.` : '';
    const ok = window.confirm(`Complete ${countPhrase} from ${label}?${skipSuffix}${otherStaffSuffix(completable)}`);
    if (!ok) return;
    setBulkProgress({ sender: g.sender, action: 'complete', done: 0, total: count, failed: 0, finished: false });
    let failed = 0;
    for (let i = 0; i < completable.length; i++) {
      try {
        await unifiedTasksApi.completeTask(completable[i].id);
      } catch {
        failed += 1;
      }
      setBulkProgress({
        sender: g.sender, action: 'complete', done: i + 1, total: count, failed, finished: i + 1 === count,
      });
    }
    fetchGroups();
    // Item 3: a completed task no longer counts as pending -- the affected
    // assignee's chip count shrinks.
    fetchRoster();
    fetchSenderTasks(g.sender);
  };

  const openAssignMemberAll = async (g: LaneClientGroup) => {
    let tasks = senderTasks[g.sender];
    if (!tasks) tasks = await fetchSenderTasks(g.sender);
    if (!tasks || tasks.length === 0) {
      window.alert('No emails to assign for this sender.');
      return;
    }
    setAssignMemberAllFor({
      sender: g.sender,
      displayName: g.displayName || g.clientName || g.sender,
      taskCount: g.taskCount,
    });
  };

  const confirmAssignMemberAll = async (member: string | null, pendingCount: number) => {
    if (!assignMemberAllFor) return;
    const tasks = senderTasks[assignMemberAllFor.sender] || [];
    const count = tasks.length;
    const label = member || 'Unassigned';
    // Staff-assignment fast-follow #1: echo the member's current pending
    // workload (threaded from MemberPickerModal's onConfirm) in the same
    // confirm dialog, e.g. "Assign 67 emails from ... to Erin (currently
    // 143 pending)?" -- doesn't apply to the Unassign option, which has no
    // "pending workload" of its own.
    const workloadSuffix = member ? ` (currently ${pendingCount} pending)` : '';
    const ok = window.confirm(
      `Assign ${describeBulkCount(count, assignMemberAllFor.taskCount)} from ${assignMemberAllFor.displayName} to ${label}${workloadSuffix}?${otherStaffSuffix(tasks)}`,
    );
    if (!ok) return;
    const { sender } = assignMemberAllFor;
    setAssignMemberAllFor(null);
    // Perf fix -- single POST /ui/team-tasks/bulk-assign call for the whole
    // sender's task list instead of one per task (same USER REPORT root
    // cause as the page-wide bulk bar's runPageBulkOneShot, applied here
    // since this per-group "Assign all -> staff" flow never went through
    // that shared runner). The bulkProgress shape (sender/action/done/
    // total/failed/finished) is unchanged -- only how it's driven: it now
    // jumps straight from 0/N to N/N (indeterminate in between, no more
    // per-item ticks) once the one request settles, rather than
    // incrementing on every one of N sequential requests. `assigned` in the
    // response is a partial *count* (bulk_assign_tasks silently skips ids
    // it can't find rather than erroring), so "failed" here means "not
    // matched", not a per-task try/catch outcome.
    setBulkProgress({ sender, action: 'assignMember', done: 0, total: count, failed: 0, finished: false });
    let failed = 0;
    let succeeded = 0;
    try {
      const { assigned } = await laneApi.assignMember(tasks.map((t) => t.id), member);
      succeeded = assigned;
      failed = Math.max(0, count - assigned);
    } catch {
      failed = count;
    }
    setBulkProgress({ sender, action: 'assignMember', done: count, total: count, failed, finished: true });
    fetchGroups();
    // Item 3: this member's (and, for a reassign, the previous assignee's)
    // pending count just changed.
    fetchRoster();
    fetchSenderTasks(sender);
    // Item 4: same rule as the page-wide bulk assign toast above -- real
    // assignment only (not Unassign), All-staff scope only.
    if (member && scope === 'all' && succeeded > 0) {
      showToastFn({
        type: 'success',
        message: `Assigned ${succeeded} to ${member} — switch to Unassigned to keep triaging`,
      });
    }
  };

  const renderGroupList = (slotProps: LaneListSlotProps) => (
    <div className="client-lane__groups">
      {/* The open task used to lift to a pinned row above all sender groups.
          Removed 2026-08-27 (Dec): rows stay in their group, and the open one
          is found by its selected styling and the scroll position you left the
          list at, not by being moved. */}
      {/* Archived browse never reaches the grouped view at all (showFlat
          forces flat for `archived`, see that field's doc comment) -- this
          guard mirrors LenderLane's identical one for explicitness/parity
          rather than because it's reachable here. `pageWideBulkAvailable`
          is the BUG 2 fix: hidden under scope='me', available under
          'unassigned'/'all' (see that constant's doc comment above). */}
      {!filters.archived && pageWideBulkAvailable && groups.length > 0 && (
        <div className="client-lane__groups-toolbar">
          <label className="client-lane__select-all">
            <input
              type="checkbox"
              aria-label="Select all on this page"
              checked={allOnPageChecked}
              disabled={selectAllLoading}
              onChange={() => toggleSelectAllOnPage(slotProps.showToast)}
            />
            {selectAllLoading
              ? 'Loading…'
              : `Select all on page (${groups.reduce((sum, g) => sum + g.taskCount, 0)})`}
          </label>
        </div>
      )}

      {!filters.archived && pageWideBulkAvailable && checkedIds.size > 0 && (
        <div className="client-lane__bulk-bar">
          <span className="client-lane__bulk-count">{checkedIds.size} selected</span>
          <div className="client-lane__bulk-actions">
            <Button size="sm" variant="primary" onClick={handlePageBulkComplete}>
              Complete
            </Button>
            <Button size="sm" variant="secondary" onClick={handlePageBulkArchiveClick}>
              Archive
            </Button>
            {/* Item 2 (lane-feedback batch 28/07, Ryan decision): "assign to
                staff" is no longer admin-only -- any authenticated staff
                member can assign a task to any roster member, matching the
                backend's POST /ui/team-tasks/bulk-assign, which moved from
                require_role(['admin']) to require_auth. Was `isAdmin &&`. */}
            <Button size="sm" variant="secondary" onClick={() => setPageBulkAssignMemberOpen(true)}>
              Assign to staff
            </Button>
          </div>
          {pageBulkProgress && (
            <span className="client-lane__bulk-progress">
              {PAGE_BULK_PROGRESS_VERB[pageBulkProgress.action]} {pageBulkProgress.done}/{pageBulkProgress.total}
            </span>
          )}
        </div>
      )}

      {/* Blocked case: every checked row is assigned to a claim, so there's
          nothing archivable -- the modal never opens (see
          `handlePageBulkArchiveClick`). Same pattern as LenderLane's
          identical `archiveBlockedMessage`. */}
      {pageArchiveBlockedMessage && (
        <div className="client-lane__archive-blocked" role="status">
          <span>{pageArchiveBlockedMessage}</span>
          <button
            type="button"
            className="client-lane__bulk-result-dismiss"
            aria-label="Dismiss"
            onClick={() => setPageArchiveBlockedMessage(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Deliberately NOT gated on checkedIds.size > 0 -- see runPageBulk's
          doc comment: it clears checkedIds in the same batch it sets this
          result, so gating the summary on the selection would hide it (incl.
          a partial-failure "N failed") the instant it appears. */}
      {pageBulkResult && (
        <div className="client-lane__bulk-result" role="status">
          <span className="client-lane__bulk-result-message">
            {pageBulkResult.failed > 0
              ? `${pageBulkResult.total - pageBulkResult.failed}/${pageBulkResult.total} ${PAGE_BULK_RESULT_SUFFIX[pageBulkResult.action]} — ${pageBulkResult.failed} failed`
              : `${pageBulkResult.total} ${PAGE_BULK_RESULT_SUFFIX[pageBulkResult.action]}`}
          </span>
          <button
            type="button"
            className="client-lane__bulk-result-dismiss"
            aria-label="Dismiss completion result"
            onClick={() => setPageBulkResult(null)}
          >
            &times;
          </button>
        </div>
      )}

      {groupsLoading && (
        <div className="lane-skeleton-rows" aria-hidden="true">
          <div className="lane-skeleton-row" />
          <div className="lane-skeleton-row" />
          <div className="lane-skeleton-row" />
        </div>
      )}
      {groupsError && <div className="client-lane__error">{groupsError}</div>}
      {/* Reviewer finding (Wave 2): under the default `scope=me`, a staffer
          with no assigned tasks -- or one not in the TEAM_MEMBERS mapping at
          all -- used to hit the generic "All caught up" copy below, which
          reads as "there's nothing to do" rather than "you have nothing
          assigned; there may still be unassigned/other-staff work". Mirrors
          the group body's "Nothing waiting from this sender." empty-state
          container (`client-lane__empty`), just with the friendlier
          title+body layout the top-level empty state already uses, plus an
          inline link/button that flips the segmented control to All staff
          -- the same `setScope` the toggle buttons above call. */}
      {!groupsLoading && !groupsError && groups.length === 0 && scope === 'me' && (
        <div className="client-lane__empty">
          <div className="client-lane__empty-title">No tasks assigned to you</div>
          <div className="client-lane__empty-body">
            Switch to{' '}
            <button
              type="button"
              className="client-lane__empty-link"
              aria-label="Switch to All staff scope"
              onClick={() => setScope('all')}
            >
              &ldquo;All staff&rdquo;
            </button>
            {' '}to see every task.
          </div>
        </div>
      )}
      {!groupsLoading && !groupsError && groups.length === 0 && scope !== 'me' && (
        <div className="client-lane__empty">
          <div className="client-lane__empty-title">All caught up</div>
          <div className="client-lane__empty-body">No client emails are waiting on a reply right now.</div>
        </div>
      )}
      {groups.map((g) => {
        const isOpen = expandedGroup === g.sender;
        const level = agingLevel(g.oldestReceived);
        return (
          <div className="client-lane__group" key={g.sender}>
            <div
              className="client-lane__group-header"
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(g.sender)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(g.sender); } }}
            >
              <span className="client-lane__group-caret">{isOpen ? '▾' : '▸'}</span>
              <span className="client-lane__group-name">{g.displayName || g.clientName || g.sender}</span>
              <span className="client-lane__group-email">{g.sender}</span>
              <span className="client-lane__group-count" title="Open emails">{g.taskCount}</span>
              <span className={`client-lane__aging client-lane__aging--${level ?? 'unknown'}`}>
                {agingLabel(g.oldestReceived)}
              </span>
              <div className="client-lane__group-actions" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="secondary" onClick={() => openAssignAll(g)}>
                  Assign all &rarr; claim
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleCompleteAll(g)}>
                  Complete all
                </Button>
                {/* Item 2: no longer admin-only -- see the page-wide bulk
                    bar's identical comment above. */}
                <Button size="sm" variant="ghost" onClick={() => openAssignMemberAll(g)}>
                  Assign all &rarr; staff
                </Button>
              </div>
            </div>
            {isOpen && (
              <div className="client-lane__group-body">
                {senderLoading[g.sender] && (
                  <div className="lane-skeleton-rows" aria-hidden="true">
                    <div className="lane-skeleton-row" />
                    <div className="lane-skeleton-row" />
                  </div>
                )}
                {senderError[g.sender] && <div className="client-lane__error">{senderError[g.sender]}</div>}
                {!senderLoading[g.sender] && !senderError[g.sender] && (senderTasks[g.sender] || []).length === 0 && (
                  <div className="client-lane__empty">Nothing waiting from this sender.</div>
                )}
                {(senderTasks[g.sender] || [])
                  .map((t) => {
                  // T14 polish: the expanded accordion rows only showed a
                  // plain day count, unlike the group header/flat list which
                  // both use the colour-coded badge -- aging colour should
                  // be consistent everywhere it's shown, not just at the
                  // group-summary level.
                  const rowLevel = agingLevel(t.received_date);
                  return (
                    <div
                      key={t.id}
                      data-task-id={t.id}
                      className={`client-lane__group-row ${slotProps.selectedTaskId === t.id ? 'client-lane__group-row--selected' : ''}`}
                    >
                      {!filters.archived && pageWideBulkAvailable && (
                        <input
                          type="checkbox"
                          className="client-lane__group-row-checkbox"
                          aria-label={`Select ${t.subject || '(no subject)'}`}
                          checked={checkedIds.has(t.id)}
                          onChange={() => toggleChecked(t.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div
                        className="client-lane__group-row-body"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask)}
                        onKeyDown={(e) => { if (e.key === 'Enter') toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask); }}
                      >
                        <span className="client-lane__row-subject">{t.subject || '(no subject)'}</span>
                        <span className={`client-lane__aging client-lane__aging--${rowLevel ?? 'unknown'}`}>
                          {agingLabel(t.received_date)}
                        </span>
                        {t.assignedToTeamMember && (
                          <span className="lane-assignee-pill">{t.assignedToTeamMember}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {bulkProgress && bulkProgress.sender === g.sender && (
                  <div className="client-lane__bulk-progress">
                    {bulkProgress.action === 'complete' ? 'Completing' : 'Assigning'} {bulkProgress.done}/{bulkProgress.total}
                    {bulkProgress.finished && (bulkProgress.failed > 0
                      ? ` — ${bulkProgress.failed} failed`
                      : ' — done')}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Friendly, context-aware empty-state copy (T14 polish sweep) -- "No
  // emails." reads the same whether the lane is just empty, mid-search, or
  // showing a read-only archive, which isn't very helpful to a staff member
  // trying to work out whether that's expected.
  const flatEmptyCopy = filters.archived
    ? 'No archived client emails match this search.'
    : filters.tab === 'completed'
      ? 'No completed client emails match this search.'
      : filters.q || filters.senderEmail || filters.claim || filters.assigned
        ? 'No client emails match this search.'
        : 'All caught up — no client emails waiting.';

  // Demo finding: "for completed emails we're missing a lot of the details
  // of who completed it and when it was completed." Aging (how long a task
  // waited before being actioned) is meaningless once it's done, so the
  // Completed sub-tab and the archived view both swap the aging badge for
  // completed-by/archived-by attribution instead -- archived takes priority
  // over completed when both apply (a row can be completed-then-archived;
  // the archive view is specifically about the archive action).
  const showCompletionInfo = filters.tab === 'completed' || filters.archived;

  const renderFlatList = (slotProps: LaneListSlotProps) => (
    <div className="client-lane__flat-list">
      {flatLoading && (
        <div className="lane-skeleton-rows" aria-hidden="true">
          <div className="lane-skeleton-row" />
          <div className="lane-skeleton-row" />
          <div className="lane-skeleton-row" />
        </div>
      )}
      {flatError && <div className="client-lane__error">{flatError}</div>}
      {!flatLoading && !flatError && flatRows.length === 0 && (
        <div className="client-lane__empty">{flatEmptyCopy}</div>
      )}
      {flatRows.map((t) => {
        const level = agingLevel(t.received_date);
        return (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            data-task-id={t.id}
            className={`client-lane__row ${slotProps.selectedTaskId === t.id ? 'client-lane__row--selected' : ''}`}
            onClick={() => toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask)}
            onKeyDown={(e) => { if (e.key === 'Enter') toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask); }}
          >
            <div className="client-lane__row-main">
              <span className="client-lane__row-subject">{t.subject || '(no subject)'}</span>
              <span className="client-lane__row-sender">{t.email_from || t.clientName || 'Unknown'}</span>
            </div>
            {showCompletionInfo ? (
              <span className="lane-completed-info">
                {filters.archived ? (
                  <>
                    {t.archivedBy && <ActorLabel raw={t.archivedBy} />}
                    {t.archivedAt && <span>{formatShortDate(t.archivedAt)}</span>}
                  </>
                ) : (
                  <>
                    {t.completedBy && <ActorLabel raw={t.completedBy} />}
                    {t.completedAt && <span>{formatShortDate(t.completedAt)}</span>}
                  </>
                )}
              </span>
            ) : (
              <span className={`client-lane__aging client-lane__aging--${level ?? 'unknown'}`}>
                {agingLabel(t.received_date)}
              </span>
            )}
            {t.assignedToTeamMember && (
              <span className="lane-assignee-pill">{t.assignedToTeamMember}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  // #408: hand-edited/deep-linked URLs could combine the advanced per-name
  // filter with the segmented control -- effectiveAssigned already resolves
  // that (the per-name filter wins), so disable the control rather than let
  // it imply a scope that isn't actually being applied.
  const scopeDisabled = !!filters.assigned;

  const renderList = (slotProps: LaneListSlotProps) => (
    <div className="client-lane">
      <RefreshBridge token={slotProps.refreshToken} onChange={setShellRefreshToken} />
      <ToastBridge fn={slotProps.showToast} onChange={setShowToastFn} />
      <div className="client-lane__toggle-row">
        <div
          className="client-lane__scope-toggle"
          role="group"
          aria-label="Filter by assignment"
          title={scopeDisabled ? 'Not applied -- a specific staff member is selected in the toolbar filter above.' : undefined}
        >
          {(['me', 'unassigned', 'all'] as AssignScope[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`client-lane__scope-btn${scope === s && !scopeDisabled ? ' client-lane__scope-btn--active' : ''}`}
              aria-pressed={scope === s && !scopeDisabled}
              disabled={scopeDisabled}
              onClick={() => setScope(s)}
            >
              {s === 'me' ? 'Mine' : s === 'unassigned' ? 'Unassigned' : 'All staff'}
            </button>
          ))}
        </div>
        <label className="client-lane__flat-toggle">
          <input
            type="checkbox"
            checked={showFlat}
            disabled={filters.tab === 'completed'}
            onChange={(e) => setFlat(e.target.checked)}
          />
          Flat list
        </label>
        {/* Item 3 (lane-feedback batch 28/07): workload chip strip -- one
            chip per roster member ("Name N", N = that member's
            client-lane-scoped pendingCount) plus an Unassigned chip, so the
            per-member workload the roster fetch already carries (see
            fetchRoster above) is visible at a glance instead of only
            surfacing inside the assign-to-staff picker. `null` while the
            roster hasn't loaded (or the fetch failed) simply renders no
            strip -- same non-fatal fallback the roster fetch itself uses. */}
        {roster && (
          <div className="client-lane__workload-strip" aria-label="Team workload">
            {roster.members.map((m) => (
              <span
                key={m.name}
                className={`client-lane__workload-chip${m.name === roster.currentUserTeamName ? ' client-lane__workload-chip--me' : ''}`}
              >
                {m.name} {m.pendingCount ?? 0}
              </span>
            ))}
            <span className="client-lane__workload-chip client-lane__workload-chip--unassigned">
              Unassigned {roster.unassignedCount}
            </span>
          </div>
        )}
      </div>
      {showFlat ? renderFlatList(slotProps) : renderGroupList(slotProps)}
    </div>
  );

  // The real TaskDetailPane (Task 12) -- fetches by id directly (never off
  // this lane's `senderTasks`/`flatRows` caches, see its own doc comment),
  // so it renders correctly even for a bare `?selected=<id>` deep link the
  // accordion never happened to expand.
  // ClientLane keeps its own group-level bulk actions (Assign all/Complete
  // all, above) as-is; this pane covers the single-task assign/reassign/
  // forward/complete/archive actions the brief calls out for all four lanes.
  const renderDetail = ({ selectedTaskId, showToast }: LaneDetailSlotProps) => (
    <TaskDetailPane
      taskId={selectedTaskId}
      // fetchGroups alone refreshes the sender-group COUNTS but not the
      // expanded group's rows, so a task completed from this pane visibly
      // lingered in the accordion until Refresh. The group-level bulk paths
      // (handleCompleteAll, confirmAssignAll) already refetch the body; this
      // single-task path was the one that didn't.
      onChanged={() => {
        if (showFlat) { fetchFlat(); return; }
        fetchGroups();
        if (expandedGroup) fetchSenderTasks(expandedGroup);
      }}
      showToast={showToast}
      emptyMessage="Select an email to view it here."
      lane="client"
    />
  );

  return (
    <>
      <LaneShell
        lane="client"
        renderList={renderList}
        renderDetail={renderDetail}
        totalCount={showFlat ? flatTotal : groupsTotal}
        // Task 15: each mode now supplies its own fresh statusCounts
        // (flatStatusCounts / groupsStatusCounts above) -- undefined only
        // pre-fetch, never a stale count carried over from the other mode,
        // since switching `showFlat` also switches which state variable is
        // read here.
        statusCounts={showFlat ? flatStatusCounts : groupsStatusCounts}
        viewportSplit
      />
      {assignAllFor && (
        <div className="lane-modal-backdrop" onClick={closeAssignAll}>
          <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="lane-modal__title">Assign all emails to a claim</h2>
            <p className="lane-modal__hint">
              {(senderTasks[assignAllFor.sender] || []).length} email(s) from {assignAllFor.displayName}
            </p>
            <ClaimPicker
              selectedClaim={selectedClaim}
              onSelect={setSelectedClaim}
              autoFocus
              collapseOnSelect={false}
            />
            <div className="lane-modal__actions">
              <Button variant="secondary" onClick={closeAssignAll}>Cancel</Button>
              <Button variant="primary" disabled={!selectedClaim} onClick={confirmAssignAll}>Assign all</Button>
            </div>
          </div>
        </div>
      )}
      {assignMemberAllFor && (
        <MemberPickerModal
          title="Assign all emails to a staff member"
          hint={`${(senderTasks[assignMemberAllFor.sender] || []).length} email(s) from ${assignMemberAllFor.displayName}`}
          confirmLabel="Assign all"
          lane="client"
          onConfirm={confirmAssignMemberAll}
          onClose={() => setAssignMemberAllFor(null)}
        />
      )}
      {/* BUG 2: page-wide select-all bulk bar's own two dialogs, distinct
          from the per-group assignAllFor/assignMemberAllFor modals above. */}
      {pageBulkAssignMemberOpen && (
        <MemberPickerModal
          title="Assign to a staff member"
          hint={`Assign ${checkedIds.size} selected email${checkedIds.size === 1 ? '' : 's'} to:${otherStaffSuffix(Array.from(checkedIds).map((id) => allFetchedTasksById[id]).filter(Boolean) as LaneTaskRow[])}`}
          confirmLabel="Assign"
          lane="client"
          processing={pageBulkAssigningMember}
          onConfirm={handlePageBulkAssignMemberConfirm}
          onClose={() => setPageBulkAssignMemberOpen(false)}
        />
      )}
      {pageBulkArchiveOpen && (
        <ArchiveReasonModal
          title="Archive selected emails"
          hint={(pageArchiveSkippedCount > 0
            ? `Archive ${pageArchivableIds.length} tasks? ${pageArchiveSkippedCount} of the selected are assigned to a claim and will be skipped.`
            : `Archive ${pageArchivableIds.length} tasks?`)
            + otherStaffSuffix(pageArchivableIds.map((id) => allFetchedTasksById[id]).filter(Boolean) as LaneTaskRow[])}
          confirmLabel="Archive emails"
          processing={pageBulkArchiving}
          onConfirm={handlePageBulkArchiveConfirm}
          onClose={() => setPageBulkArchiveOpen(false)}
        />
      )}
    </>
  );
};

export default ClientLane;
